import {
    AdjustmentsHorizontalIcon,
    CheckIcon,
    ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
    Accordion,
    Alert,
    Badge,
    Button,
    Center,
    Container,
    Group,
    Loader,
    NumberInput,
    Paper,
    Stack,
    Switch,
    Text,
    Textarea,
    TextInput,
    Title,
    useMantineColorScheme,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { UserRole } from "../../models/user.model";
import { profileCompletionAdminService } from "../../services/profileCompletionAdminService";
import {
    ProfileCompletionConfigDto,
    ProfileCompletionRuleConfigDto,
    RuleParameterDto,
    RuleTextDto,
} from "../../types/profileCompletionAdmin";

// Local draft state tracking per-rule edits
interface RuleDraft {
    description: string;
    tip: string;
    reasoningTemplate: string;
    parameterValues: Record<number, string>; // parameterId → value
    isDirty: boolean;
}

const buildInitialDraft = (rule: ProfileCompletionRuleConfigDto): RuleDraft => ({
    description: rule.englishText?.description ?? "",
    tip: rule.englishText?.tip ?? "",
    reasoningTemplate: rule.englishText?.reasoningTemplate ?? "",
    parameterValues: Object.fromEntries(
        rule.parameters.map((p) => [p.id, p.parameterValue])
    ),
    isDirty: false,
});

const REASONING_TEMPLATE_HELP =
    "Use positional placeholders {0}, {1}, {2}… matching the rule strategy contract. " +
    "Example: 'Has {0} items (minimum {1}, awarded {2} pts)'. " +
    "Removing an existing placeholder may break display.";

export const OperationsProfileCompletionConfig: React.FC = () => {
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === "dark";
    const { userRole } = useAuth();
    const isAdmin = userRole === UserRole.Admin;

    const [config, setConfig] = useState<ProfileCompletionConfigDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [drafts, setDrafts] = useState<Record<number, RuleDraft>>({});
    const [savingRule, setSavingRule] = useState<number | null>(null);
    const [togglingRule, setTogglingRule] = useState<number | null>(null);
    // sectionId → draft weight string (e.g. "20" = 20%)
    const [weightDrafts, setWeightDrafts] = useState<Record<number, string>>({});
    const [savingWeight, setSavingWeight] = useState<number | null>(null);

    useEffect(() => {
        const loadConfig = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await profileCompletionAdminService.getConfig();
                setConfig(data);
                const initialDrafts: Record<number, RuleDraft> = {};
                const initialWeightDrafts: Record<number, string> = {};
                data.sections.forEach((section) => {
                    initialWeightDrafts[section.sectionId] = (section.weight * 100).toFixed(0);
                    section.rules.forEach((rule) => {
                        initialDrafts[rule.sectionRuleId] = buildInitialDraft(rule);
                    });
                });
                setDrafts(initialDrafts);
                setWeightDrafts(initialWeightDrafts);
            } catch (err) {
                setError("Failed to load profile completion configuration.");
                console.error("[ProfileCompletionConfig] Load error:", err);
            } finally {
                setLoading(false);
            }
        };
        loadConfig();
    }, []);

    const handleDraftChange = (
        ruleId: number,
        field: keyof Omit<RuleDraft, "parameterValues" | "isDirty">,
        value: string
    ) => {
        setDrafts((prev) => ({
            ...prev,
            [ruleId]: { ...prev[ruleId], [field]: value, isDirty: true },
        }));
    };

    const handleParameterChange = (
        ruleId: number,
        parameterId: number,
        value: string
    ) => {
        setDrafts((prev) => ({
            ...prev,
            [ruleId]: {
                ...prev[ruleId],
                parameterValues: { ...prev[ruleId].parameterValues, [parameterId]: value },
                isDirty: true,
            },
        }));
    };

    const handleSaveRule = async (rule: ProfileCompletionRuleConfigDto) => {
        const draft = drafts[rule.sectionRuleId];
        if (!draft) {
            return;
        }

        setSavingRule(rule.sectionRuleId);
        try {
            // Save text
            await profileCompletionAdminService.updateRuleText(rule.sectionRuleId, {
                description: draft.description || null,
                tip: draft.tip || null,
                reasoningTemplate: draft.reasoningTemplate || null,
            });

            // Save each changed non-Points parameter
            for (const param of rule.parameters) {
                if (param.parameterName.toLowerCase() === "points") {
                    continue;
                }
                const newValue = draft.parameterValues[param.id];
                if (newValue !== undefined && newValue !== param.parameterValue) {
                    await profileCompletionAdminService.updateRuleParameter(param.id, {
                        parameterValue: newValue,
                    });
                }
            }

            // Mark as clean and update local config
            setDrafts((prev) => ({
                ...prev,
                [rule.sectionRuleId]: { ...prev[rule.sectionRuleId], isDirty: false },
            }));

            setConfig((prev) => {
                if (!prev) {
                    return prev;
                }
                return {
                    ...prev,
                    sections: prev.sections.map((s) => ({
                        ...s,
                        rules: s.rules.map((r) => {
                            if (r.sectionRuleId !== rule.sectionRuleId) {
                                return r;
                            }
                            const updatedText: RuleTextDto = {
                                id: r.englishText?.id ?? 0,
                                description: draft.description || null,
                                tip: draft.tip || null,
                                reasoningTemplate: draft.reasoningTemplate || null,
                            };
                            const updatedParams: RuleParameterDto[] = r.parameters.map(
                                (p) => ({
                                    ...p,
                                    parameterValue:
                                        draft.parameterValues[p.id] ?? p.parameterValue,
                                })
                            );
                            return { ...r, englishText: updatedText, parameters: updatedParams };
                        }),
                    })),
                };
            });

            notifications.show({
                title: "Saved",
                message: `Rule "${rule.ruleName}" updated successfully.`,
                color: "green",
                icon: <CheckIcon width={16} />,
            });
        } catch (err: any) {
            const message =
                err?.responseBody?.error ??
                (err instanceof Error ? err.message : "Save failed");
            notifications.show({
                title: "Save failed",
                message,
                color: "red",
                icon: <ExclamationTriangleIcon width={16} />,
            });
            console.error("[ProfileCompletionConfig] Save error:", err);
        } finally {
            setSavingRule(null);
        }
    };

    const handleToggleRule = async (rule: ProfileCompletionRuleConfigDto) => {
        setTogglingRule(rule.sectionRuleId);
        try {
            const newEnabled = await profileCompletionAdminService.toggleRuleEnabled(
                rule.sectionRuleId
            );

            setConfig((prev) => {
                if (!prev) {
                    return prev;
                }
                return {
                    ...prev,
                    sections: prev.sections.map((s) => ({
                        ...s,
                        rules: s.rules.map((r) =>
                            r.sectionRuleId === rule.sectionRuleId
                                ? { ...r, isEnabled: newEnabled }
                                : r
                        ),
                    })),
                };
            });

            notifications.show({
                title: newEnabled ? "Rule enabled" : "Rule disabled",
                message: `"${rule.ruleName}" is now ${newEnabled ? "enabled" : "disabled"}.`,
                color: newEnabled ? "green" : "orange",
            });
        } catch (err) {
            notifications.show({
                title: "Toggle failed",
                message: "Could not toggle rule status.",
                color: "red",
            });
        } finally {
            setTogglingRule(null);
        }
    };

    const handleSaveWeight = async (sectionId: number, sectionName: string) => {
        const raw = weightDrafts[sectionId];
        const percent = parseFloat(raw);
        if (isNaN(percent) || percent < 0 || percent > 100) {
            notifications.show({
                title: "Invalid weight",
                message: "Weight must be a number between 0 and 100.",
                color: "red",
            });
            return;
        }
        const weight = parseFloat((percent / 100).toFixed(4));

        // Warn if saving this weight would leave the overall total != 100%
        const proposedSavedTotal = config!.sections.reduce((sum, s) => {
            const w = s.sectionId === sectionId ? percent : s.weight * 100;
            return sum + w;
        }, 0);
        if (Math.abs(proposedSavedTotal - 100) >= 0.5) {
            const delta = proposedSavedTotal - 100;
            notifications.show({
                title: "Weights don't add up to 100%",
                message: `After saving, total will be ${proposedSavedTotal.toFixed(0)}% (${delta > 0 ? "+" : ""}${delta.toFixed(0)}%). Scores may not reflect expectations until all weights are corrected.`,
                color: "orange",
                icon: <ExclamationTriangleIcon width={16} />,
                autoClose: 8000,
            });
        }

        setSavingWeight(sectionId);
        try {
            await profileCompletionAdminService.updateSectionWeight(sectionId, { weight });
            setConfig((prev) => {
                if (!prev) {
                    return prev;
                }
                return {
                    ...prev,
                    sections: prev.sections.map((s) =>
                        s.sectionId === sectionId ? { ...s, weight } : s
                    ),
                };
            });
            notifications.show({
                title: "Weight saved",
                message: `"${sectionName}" weight updated to ${percent.toFixed(0)}%.`,
                color: "green",
                icon: <CheckIcon width={16} />,
            });
        } catch (err: any) {
            const message =
                err?.responseBody?.error ??
                (err instanceof Error ? err.message : "Save failed");
            notifications.show({
                title: "Save failed",
                message,
                color: "red",
                icon: <ExclamationTriangleIcon width={16} />,
            });
        } finally {
            setSavingWeight(null);
        }
    };

    // Live total of all section draft weights — recomputed on every keystroke
    const draftTotalWeight = config
        ? config.sections.reduce((sum, s) => {
              const val = parseFloat(weightDrafts[s.sectionId] ?? "0");
              return sum + (isNaN(val) ? 0 : val);
          }, 0)
        : 0;
    const weightsBalanced = Math.abs(draftTotalWeight - 100) < 0.5;

    if (loading) {
        return (
            <Container size="xl" py="xl">
                <Center h={200}>
                    <Loader size="lg" />
                </Center>
            </Container>
        );
    }

    if (error || !config) {
        return (
            <Container size="xl" py="xl">
                <Alert
                    color="red"
                    icon={<ExclamationTriangleIcon width={18} />}
                    title="Load error"
                >
                    {error ?? "No active configuration set found."}
                </Alert>
            </Container>
        );
    }

    return (
        <Container size="xl" py="md">
            <Stack gap="md">
                <Stack gap="xs">
                    <Breadcrumb
                        items={[
                            { label: "Operations Panel", href: "/operations" },
                            { label: "Profile Completion Config", isActive: true },
                        ]}
                    />
                    <Group align="center" justify="space-between">
                        <Stack gap={0}>
                            <Title order={1}>Profile Completion Config</Title>
                            <Text c="dimmed">
                                Edit rule texts, reasoning templates, and parameter thresholds.
                                Active set:{" "}
                                <Text component="span" fw={600}>
                                    {config.setName}
                                </Text>
                            </Text>
                        </Stack>
                        <Badge
                            color="green"
                            variant="light"
                            leftSection={<AdjustmentsHorizontalIcon width={12} />}
                        >
                            {config.isActive ? "Active" : "Inactive"}
                        </Badge>
                    </Group>
                </Stack>

                {!weightsBalanced && (
                    <Alert
                        color="orange"
                        icon={<ExclamationTriangleIcon width={18} />}
                        title="Section weights don't add up to 100%"
                    >
                        <Text size="sm">
                            Current total:{" "}
                            <Text component="span" fw={700}>
                                {draftTotalWeight.toFixed(0)}%
                            </Text>
                            {draftTotalWeight < 100
                                ? ` — ${(100 - draftTotalWeight).toFixed(0)}% missing. Scores will be capped below 100% even for fully complete profiles.`
                                : ` — ${(draftTotalWeight - 100).toFixed(0)}% over budget. Scores may exceed 100%.`}
                        </Text>
                    </Alert>
                )}

                <Accordion variant="separated" multiple>
                    {config.sections.map((section) => (
                        <Accordion.Item key={section.sectionId} value={String(section.sectionId)}>
                            <Accordion.Control>
                                <Group>
                                    <Text fw={600}>{section.sectionName}</Text>
                                    <Badge variant="outline" size="sm">
                                        Weight: {(section.weight * 100).toFixed(0)}%
                                    </Badge>
                                    {!section.isEnabled && (
                                        <Badge color="gray" size="sm">
                                            Disabled
                                        </Badge>
                                    )}
                                    <Badge color="brand" variant="light" size="sm">
                                        {section.rules.filter((r) => r.isEnabled).length}/
                                        {section.rules.length} rules active
                                    </Badge>
                                </Group>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <Stack gap="md">
                                    {/* Section weight editor — admin only */}
                                    <Paper
                                        p="sm"
                                        withBorder
                                        bg={isDark ? "dark.7" : "gray.0"}
                                        style={{
                                            borderColor:
                                                weightDrafts[section.sectionId] !==
                                                    (section.weight * 100).toFixed(0)
                                                    ? "var(--mantine-color-yellow-5)"
                                                    : undefined,
                                        }}
                                    >
                                        <Group align="flex-end" gap="sm">
                                            <NumberInput
                                                label="Section weight (%)"
                                                description={
                                                    isAdmin
                                                        ? "Contribution of this section to the total score (0–100)."
                                                        : "Only admins can change section weights."
                                                }
                                                value={weightDrafts[section.sectionId] ?? (section.weight * 100).toFixed(0)}
                                                onChange={(val) =>
                                                    setWeightDrafts((prev) => ({
                                                        ...prev,
                                                        [section.sectionId]: String(val ?? ""),
                                                    }))
                                                }
                                                suffix="%"
                                                min={0}
                                                max={100}
                                                decimalScale={0}
                                                readOnly={!isAdmin}
                                                disabled={savingWeight === section.sectionId}
                                                size="sm"
                                                style={{ width: 160 }}
                                            />
                                            {isAdmin && (
                                                <>
                                                    <Button
                                                        size="xs"
                                                        variant="default"
                                                        disabled={
                                                            weightDrafts[section.sectionId] ===
                                                            (section.weight * 100).toFixed(0)
                                                        }
                                                        onClick={() =>
                                                            setWeightDrafts((prev) => ({
                                                                ...prev,
                                                                [section.sectionId]: (section.weight * 100).toFixed(0),
                                                            }))
                                                        }
                                                    >
                                                        Discard
                                                    </Button>
                                                    <Button
                                                        size="xs"
                                                        disabled={
                                                            weightDrafts[section.sectionId] ===
                                                            (section.weight * 100).toFixed(0)
                                                        }
                                                        loading={savingWeight === section.sectionId}
                                                        onClick={() =>
                                                            handleSaveWeight(
                                                                section.sectionId,
                                                                section.sectionName
                                                            )
                                                        }
                                                    >
                                                        Save weight
                                                    </Button>
                                                </>
                                            )}
                                        </Group>
                                    </Paper>

                                    {section.rules.map((rule) => {
                                        const draft = drafts[rule.sectionRuleId];
                                        const isSaving = savingRule === rule.sectionRuleId;
                                        const isToggling = togglingRule === rule.sectionRuleId;
                                        const hasPlaceholderWarning =
                                            draft?.isDirty &&
                                                rule.englishText?.reasoningTemplate
                                                ? (() => {
                                                    const original = rule.englishText!.reasoningTemplate!;
                                                    const placeholderRegex = /\{(\d+)\}/g;
                                                    let match: RegExpExecArray | null;
                                                    while (
                                                        (match = placeholderRegex.exec(original)) !== null
                                                    ) {
                                                        if (
                                                            !draft.reasoningTemplate.includes(
                                                                `{${match[1]}}`
                                                            )
                                                        ) {
                                                            return true;
                                                        }
                                                    }
                                                    return false;
                                                })()
                                                : false;

                                        return (
                                            <Paper
                                                key={rule.sectionRuleId}
                                                p="md"
                                                withBorder
                                                style={{
                                                    borderColor: draft?.isDirty
                                                        ? "var(--mantine-color-yellow-5)"
                                                        : undefined,
                                                }}
                                            >
                                                <Stack gap="sm">
                                                    <Group justify="space-between" align="flex-start">
                                                        <Stack gap={2}>
                                                            <Group gap="xs">
                                                                <Text fw={600} size="sm">
                                                                    {rule.ruleName}
                                                                </Text>
                                                                {draft?.isDirty && (
                                                                    <Badge
                                                                        color="yellow"
                                                                        size="xs"
                                                                        variant="filled"
                                                                    >
                                                                        Unsaved
                                                                    </Badge>
                                                                )}
                                                            </Group>
                                                        </Stack>
                                                        <Switch
                                                            checked={rule.isEnabled}
                                                            onChange={() => handleToggleRule(rule)}
                                                            disabled={isToggling || isSaving}
                                                            label={rule.isEnabled ? "Enabled" : "Disabled"}
                                                            size="sm"
                                                        />
                                                    </Group>

                                                    {draft && (
                                                        <>
                                                            <TextInput
                                                                label="Description"
                                                                description="Shown in admin UI — explains what this rule checks."
                                                                value={draft.description}
                                                                onChange={(e) =>
                                                                    handleDraftChange(
                                                                        rule.sectionRuleId,
                                                                        "description",
                                                                        e.currentTarget.value
                                                                    )
                                                                }
                                                                disabled={isSaving}
                                                                size="sm"
                                                            />
                                                            <TextInput
                                                                label="Tip"
                                                                description="Actionable guidance shown to CV authors when this rule fails."
                                                                value={draft.tip}
                                                                onChange={(e) =>
                                                                    handleDraftChange(
                                                                        rule.sectionRuleId,
                                                                        "tip",
                                                                        e.currentTarget.value
                                                                    )
                                                                }
                                                                disabled={isSaving}
                                                                size="sm"
                                                            />
                                                            <Textarea
                                                                label="Reasoning Template"
                                                                description={REASONING_TEMPLATE_HELP}
                                                                value={draft.reasoningTemplate}
                                                                onChange={(e) =>
                                                                    handleDraftChange(
                                                                        rule.sectionRuleId,
                                                                        "reasoningTemplate",
                                                                        e.currentTarget.value
                                                                    )
                                                                }
                                                                disabled={isSaving}
                                                                autosize
                                                                minRows={2}
                                                                size="sm"
                                                            />

                                                            {hasPlaceholderWarning && (
                                                                <Alert
                                                                    color="yellow"
                                                                    icon={
                                                                        <ExclamationTriangleIcon width={16} />
                                                                    }
                                                                    title="Placeholder removed"
                                                                    p="xs"
                                                                >
                                                                    One or more positional placeholders from the
                                                                    original template appear to be missing. This
                                                                    may break the display text for this rule.
                                                                </Alert>
                                                            )}

                                                            {draft.reasoningTemplate && (
                                                                <Paper
                                                                    p="xs"
                                                                    bg={isDark ? "dark.6" : "gray.0"}
                                                                    withBorder
                                                                >
                                                                    <Text size="xs" c="dimmed" mb={4}>
                                                                        Preview (sample values substituted):
                                                                    </Text>
                                                                    <Text size="sm" ff="monospace">
                                                                        {draft.reasoningTemplate.replace(
                                                                            /\{(\d+)\}/g,
                                                                            (_, n) => `[arg${n}]`
                                                                        )}
                                                                    </Text>
                                                                </Paper>
                                                            )}

                                                            {rule.parameters.length > 0 && (
                                                                <Stack gap="xs">
                                                                    <Text size="sm" fw={500}>
                                                                        Parameters
                                                                    </Text>
                                                                    {rule.parameters.map((param) => {
                                                                        const isPoints = param.parameterName.toLowerCase() === "points";
                                                                        return (
                                                                            <TextInput
                                                                                key={param.id}
                                                                                label={`${param.parameterName} (${param.dataType})`}
                                                                                description={isPoints ? "Read-only — contact a developer to change point values." : undefined}
                                                                                value={
                                                                                    draft.parameterValues[param.id] ??
                                                                                    param.parameterValue
                                                                                }
                                                                                onChange={
                                                                                    isPoints
                                                                                        ? undefined
                                                                                        : (e) =>
                                                                                            handleParameterChange(
                                                                                                rule.sectionRuleId,
                                                                                                param.id,
                                                                                                e.currentTarget.value
                                                                                            )
                                                                                }
                                                                                readOnly={isPoints}
                                                                                disabled={!isPoints && isSaving}
                                                                                size="sm"
                                                                            />
                                                                        );
                                                                    })}
                                                                </Stack>
                                                            )}

                                                            <Group justify="flex-end">
                                                                <Button
                                                                    size="xs"
                                                                    variant="default"
                                                                    disabled={!draft.isDirty || isSaving}
                                                                    onClick={() => {
                                                                        setDrafts((prev) => ({
                                                                            ...prev,
                                                                            [rule.sectionRuleId]:
                                                                                buildInitialDraft(rule),
                                                                        }));
                                                                    }}
                                                                >
                                                                    Discard
                                                                </Button>
                                                                <Button
                                                                    size="xs"
                                                                    disabled={!draft.isDirty}
                                                                    loading={isSaving}
                                                                    onClick={() => handleSaveRule(rule)}
                                                                >
                                                                    Save changes
                                                                </Button>
                                                            </Group>
                                                        </>
                                                    )}
                                                </Stack>
                                            </Paper>
                                        );
                                    })}
                                </Stack>
                            </Accordion.Panel>
                        </Accordion.Item>
                    ))}
                </Accordion>
            </Stack>
        </Container>
    );
};
